import PropTypes from 'prop-types';
import {
  FilteringState,
  IntegratedFiltering,
  SortingState,
  IntegratedSorting,
  SelectionState,
} from '@devexpress/dx-react-grid';
import {GridExporter} from '@devexpress/dx-react-grid-export';
import {saveAs} from 'file-saver';
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableFilterRow,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import {useWindowResize} from '@ozgene/gp5.hooks.use-window-resize';
import {useCallback, useMemo, useState, useEffect, forwardRef} from 'react';
import {sortByProperty} from '@ozgene/gp5.utils.sort-functions';
import {formatDateShort} from '@ozgene/gp5.utils.date-functions';
import {
  DateProvider,
  getDatePredicate,
} from '@ozgene/gp5.components.date-provider';
import {
  filterEquals,
  FilterIcon,
  filterMessages,
  getFilterArrayForProperty,
  getGroupFilterEquals,
} from '@ozgene/gp5.utils.filter-functions';
import {debounce} from 'throttle-debounce';
import RowsProvider from 'Utility/RowsProvider';
import ListProvider from 'SbsLobby/Components/ListProvider';
import {ColumnDefinedFilterCell} from 'Components/ColumnDefinedFilterCell';
import {ColumnDefinedCell} from 'Components/ColumnDefinedCell';
import {CustomNoDataCell} from 'Components/CustomNoDataCell';
import SelectTeam from 'Teams/components/SelectTeam';
import SelectSbsStatus from 'Sbs/components/SelectSbsStatus';
import EntityFilterCell from 'Components/EntityFilterCell';
import {CustomRowComponent} from 'Components/CustomRowComponent';
import SbsStatusProvider from 'SbsLobby/Components/SbsStatusProvider';
import SelectSbsUser from 'Components/SelectSbsUser';

const userFilterEquals = getGroupFilterEquals(({fullName}) => fullName);
const sbsColumnFilters = [
  {
    columnName: 'title',
    predicate: () => true,
  },
  {
    columnName: 'teams',
    predicate: getFilterArrayForProperty(({name}) => name),
  },
  {
    columnName: 'sbsStatus',
    predicate: filterEquals,
  },
  {
    columnName: 'tags',
    predicate: getFilterArrayForProperty(({name}) => name),
  },
  {
    columnName: 'modifiedDt',
    predicate: getDatePredicate,
  },
  {
    columnName: 'modifiedBy',
    predicate: userFilterEquals,
  },
];
const sbsColumnExtensions = [
  {
    columnName: 'teams',
    width: 200,
    wordWrapEnabled: true,
  },
  {
    columnName: 'title',
    wordWrapEnabled: true,
    compare: sortByProperty((x) => x),
  },
  {
    columnName: 'sbsStatus',
    width: 150,
    wordWrapEnabled: true,
  },
  {
    columnName: 'tags',
    wordWrapEnabled: true,
  },
  {
    columnName: 'modifiedDt',
    width: 150,
    wordWrapEnabled: true,
  },
  {
    columnName: 'modifiedBy',
    wordWrapEnabled: true,
  },
];
const sbsColumns = [
  {
    name: 'teams',
    title: 'Teams',
    getCellValue: (row) => row.teams,
    getLabel: ({name}) => name,
    getId: ({id}) => id,
    CustomCellFilterComponent: EntityFilterCell,
    customCellFilterProps: {
      SelectEntity: SelectTeam,
      getValueIdentifier: ({name}) => name,
    },
    customiseCell: (row) => row.teams.map(({name}) => name).join(', '),
  },
  {
    name: 'title',
    title: 'Title',
    getCellValue: (row) => row.title,
  },
  {
    name: 'sbsStatus',
    title: 'Status',
    getCellValue: (row) => row.sbsStatus.status,
    CustomCellFilterComponent: EntityFilterCell,
    customCellFilterProps: {
      SelectEntity: SelectSbsStatus,
      placeholder: 'Filter...',
      getValueIdentifier: ({status}) => status,
    },
  },
  {
    name: 'tags',
    title: 'Tags',
    getCellValue: (row) => row.tags,
    customiseCell: (row) => row.tags.map(({name}) => name).join(', '),
  },
  {
    name: 'modifiedDt',
    title: 'Modified Date',
    getCellValue: (row) => row.modifiedDt,
    customiseCell: (row) => formatDateShort(row.modifiedDt),
  },
  {
    name: 'modifiedBy',
    title: 'Modified By',
    getCellValue: (row) =>
      row.modifiedBy.fullName === 'null null'
        ? row.modifiedBy.username
        : row.modifiedBy.fullName,
    CustomCellFilterComponent: EntityFilterCell,
    customCellFilterProps: {
      SelectEntity: SelectSbsUser,
      placeholder: 'Filter...',
      style: {paddingLeft: 10},
      showInactiveUsers: true,
      useUsername: ({fullName}) => (fullName === 'null null' ? true : false),
    },
  },
];
const SbsList = forwardRef(function SbsList(props, ref) {
  const {
    sbss,
    isLoading,
    onChangeSelection,
    selectedSbsId,
    sbsTitleFilter,
    sbsTeamFilter,
    onChangeSbsTitleFilter,
    onChangeSbsTeamFilter,
  } = props;
  const {height} = useWindowResize();
  const [filters, setFilters] = useState([
    sbsTitleFilter.length && {
      columnName: 'title',
      operation: 'contains',
      value: sbsTitleFilter,
    },
    sbsTeamFilter.length && {
      columnName: 'teams',
      value: sbsTeamFilter,
    },
  ]);
  const [exportedRows, setExportedRows] = useState([]);
  const messages = useMemo(
    () => ({
      noData:
        sbsTitleFilter.length >= 2 || sbsTeamFilter
          ? 'No Data'
          : 'Please select a team or enter a title to search',
    }),
    [sbsTeamFilter, sbsTitleFilter],
  );
  // for some unknown reason this tries to execute multiple times
  // so we debounce to ensure it only gets executed once
  // but we need to useMemo to ensure that it isn't recreated each time
  const handleSaveList = useMemo(
    () =>
      debounce(
        2000,
        async (data) => {
          const buffer = await data.xlsx.writeBuffer();

          saveAs(
            new Blob([buffer], {type: 'application/octet-stream'}),
            'SBS_List.xlsx',
          );
        },
        {atBegin: true},
      ),
    [],
  );
  const handleCustomiseCell = (cell, row, column) => {
    if (column.customiseCell) {
      cell.value = column.customiseCell(row);
    }
  };
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const titleFilter = newFilters.find(
        ({columnName}) => columnName === 'title',
      );
      const titleFilterValue = titleFilter?.value ?? '';

      if (titleFilterValue !== sbsTitleFilter) {
        onChangeSbsTitleFilter(titleFilterValue);
      }

      const teamFilter = newFilters.find(
        ({columnName}) => columnName === 'teams',
      );
      const teamFilterValue = teamFilter?.value ?? '';

      if (teamFilterValue !== sbsTeamFilter) {
        onChangeSbsTeamFilter(teamFilterValue);
      }
      setFilters(newFilters);
    },
    [
      onChangeSbsTeamFilter,
      onChangeSbsTitleFilter,
      sbsTeamFilter,
      sbsTitleFilter,
    ],
  );

  useEffect(
    () =>
      setFilters((prevFilters) => [
        ...prevFilters.filter(
          (filter) => filter && !['title', 'teams'].includes(filter.columnName),
        ),
        sbsTitleFilter.length && {
          columnName: 'title',
          operation: 'contains',
          value: sbsTitleFilter,
        },
        sbsTeamFilter.length && {
          columnName: 'teams',
          value: sbsTeamFilter,
        },
      ]),
    [sbsTitleFilter, sbsTeamFilter],
  );

  return (
    <>
      <Grid rows={sbss} columns={sbsColumns} getRowId={({id}) => id}>
        <DateProvider for={['modifiedDt']} />
        <ListProvider for={['teams', 'tags']} />
        <SbsStatusProvider for={['sbsStatus']} />
        <FilteringState
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
        <SortingState />
        <SelectionState
          selection={[selectedSbsId]}
          onSelectionChange={onChangeSelection}
        />
        <IntegratedFiltering columnExtensions={sbsColumnFilters} />
        <IntegratedSorting columnExtensions={sbsColumnExtensions} />
        <RowsProvider onRowsUpdate={setExportedRows} />
        <VirtualTable
          columnExtensions={sbsColumnExtensions}
          height={height - (isLoading ? 134 : 129)}
          cellComponent={ColumnDefinedCell}
          noDataCellComponent={CustomNoDataCell}
          messages={messages}
        />
        <TableHeaderRow showSortingControls />
        <TableSelection
          selectByRowClick
          highlightRow
          showSelectionColumn={false}
          rowComponent={CustomRowComponent}
        />
        <TableFilterRow
          showFilterSelector
          cellComponent={ColumnDefinedFilterCell}
          iconComponent={FilterIcon}
          messages={filterMessages}
        />
      </Grid>

      <GridExporter
        ref={ref}
        rows={exportedRows}
        columns={sbsColumns}
        onSave={handleSaveList}
        customizeCell={handleCustomiseCell}
        sorting={[{columnName: 'title', direction: 'asc'}]}
      />
    </>
  );
});

SbsList.propTypes = {
  sbss: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedSbsId: PropTypes.string,
  sbsTitleFilter: PropTypes.string,
  sbsTeamFilter: PropTypes.string,
  isLoading: PropTypes.bool,
  onChangeSelection: PropTypes.func,
  onChangeSbsTitleFilter: PropTypes.func.isRequired,
  onChangeSbsTeamFilter: PropTypes.func.isRequired,
};

SbsList.defaultProps = {
  isLoading: false,
  onChangeSelection: undefined,
  sbsTitleFilter: '',
  sbsTeamFilter: '',
};

export default SbsList;
